<template>
  <div class="file-review-container">
    <!-- Fixed Header Section -->
    <div class="fixed-header">
      <v-toolbar flat color="white" class="mb-2">
        <div>
          <div class="headline">File Review Notes</div>
          <div class="subtitle-2 grey--text">
            {{ currentPageDisplay }} of {{ formatCount(totalCount) }} notes
            <span v-if="selectedUser">
              - Filtered by {{ selectedUser.firstName }}
              {{ selectedUser.lastName }}
            </span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <div class="mr-3" style="width: 250px">
          <v-select
            v-model="selectedUserId"
            :items="userOptions"
            label="Filter by User"
            outlined
            dense
            hide-details
            clearable
            @change="onUserFilterChange"
          ></v-select>
        </div>
        <div class="mr-3" style="width: 200px">
          <v-select
            v-model="sortOrder"
            :items="sortOptions"
            label="Sort by"
            outlined
            dense
            hide-details
          ></v-select>
        </div>
        <div style="width: 300px">
          <v-text-field
            v-model="search"
            placeholder="Search notes..."
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>
      </v-toolbar>

      <!-- Pagination Controls -->
      <v-toolbar flat color="white" class="mb-2">
        <v-spacer></v-spacer>
        <v-btn
          :disabled="!hasPreviousPage || loading"
          @click="loadPreviousPage"
          color="primary"
          outlined
          class="mr-2"
        >
          <v-icon left>mdi-chevron-left</v-icon>
          Previous
        </v-btn>
        <v-btn
          :disabled="!hasNextPage || loading"
          @click="loadNextPage"
          color="primary"
          outlined
        >
          Next
          <v-icon right>mdi-chevron-right</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Loading State -->
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
      ></v-progress-linear>
    </div>

    <!-- Scrollable Cards Section -->
    <div class="scrollable-content">
      <v-container fluid>
        <v-row>
          <v-col v-for="note in filteredNotes" :key="note.noteId" cols="12">
            <v-card class="file-review-card" elevation="2">
              <!-- Card Header -->
              <v-card-title class="pb-2">
                <v-row align="center" no-gutters>
                  <v-col cols="auto">
                    <v-icon color="primary" class="mr-2"
                      >mdi-file-document-outline</v-icon
                    >
                  </v-col>
                  <v-col>
                    <div class="d-flex align-center">
                      <div class="text-h6 mr-3">{{ note.clientName }}</div>
                      <v-chip
                        small
                        :color="getCaseStatusColor(note.caseStatus)"
                        dark
                        label
                      >
                        <v-icon left small>mdi-folder-open</v-icon>
                        {{ note.caseStatus }}
                      </v-chip>
                    </div>
                    <div class="text-caption grey--text">
                      Case #{{ note.caseNumber }}
                    </div>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      small
                      color="primary"
                      :href="
                        `https://cs-web.airdesksolutions.com/SA/CaseSummary.aspx?CaseID=${note.caseId}`
                      "
                      target="_blank"
                    >
                      <v-icon left small>mdi-open-in-new</v-icon>
                      View Case
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-title>

              <v-divider></v-divider>

              <!-- Card Metadata -->
              <v-card-subtitle class="py-3">
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <v-chip small outlined color="primary">
                      <v-icon left small>mdi-account</v-icon>
                      {{ getCreatorName(note.creator) }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-chip small outlined color="grey">
                      <v-icon left small>mdi-calendar</v-icon>
                      {{ formatDate(note.createdDate) }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" sm="4" v-if="note.noteDescription">
                    <v-chip small outlined color="grey">
                      <v-icon left small>mdi-tag</v-icon>
                      {{ note.noteDescription }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-subtitle>

              <v-divider></v-divider>

              <!-- Note Content -->
              <v-card-text class="py-4">
                <div
                  class="note-content-html"
                  v-html="note.notePlainText"
                ></div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Empty State -->
        <v-row v-if="filteredNotes.length === 0 && !loading">
          <v-col cols="12" class="text-center py-12">
            <v-icon size="64" color="grey">mdi-file-document-outline</v-icon>
            <div class="text-h6 grey--text mt-4">
              No file review notes found
            </div>
            <div class="text-body-2 grey--text">
              {{
                search
                  ? "Try adjusting your search criteria"
                  : "There are no notes to display"
              }}
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import format from "date-fns/format";
import GetFileReviewNotes from "@/graphql/FileReviewNotes.gql";

export default {
  data() {
    return {
      search: "",
      fileReviewNotes: [],
      users: [],
      loading: false,
      currentCursor: null,
      startCursor: null,
      endCursor: null,
      hasNextPage: false,
      hasPreviousPage: false,
      totalCount: 0,
      cursorHistory: [], // Track cursor history for back navigation
      sortOrder: "newest",
      sortOptions: [
        { text: "Newest First", value: "newest" },
        { text: "Oldest First", value: "oldest" },
        { text: "Client Name (A-Z)", value: "client-asc" },
        { text: "Client Name (Z-A)", value: "client-desc" },
      ],
      selectedUserId: null,
    };
  },
  apollo: {
    organization: {
      query: gql`
        query($id: ID!) {
          organization(id: $id) {
            id
            name
            users {
              id
              firstName
              lastName
              staffCode
              saContactId
              active
            }
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id,
        };
      },
      update(data) {
        if (data && data.organization && data.organization.users) {
          this.users = data.organization.users.filter((user) => user.active);
        }
        return data;
      },
    },
  },
  computed: {
    ...mapGetters(["currentUser"]),
    userOptions() {
      if (!this.users || this.users.length === 0) return [];

      return [
        { text: "All Users", value: null },
        ...this.users
          .filter((user) => user.saContactId)
          .map((user) => ({
            text: `${user.firstName} ${user.lastName} (${user.staffCode})`,
            value: user.id,
          }))
          .sort((a, b) => a.text.localeCompare(b.text)),
      ];
    },
    selectedUser() {
      if (!this.selectedUserId || !this.users) return null;
      return this.users.find((u) => u.id === this.selectedUserId);
    },
    filteredNotes() {
      let notes = [...this.fileReviewNotes];

      // Apply search filter
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        notes = notes.filter((note) => {
          return (
            note.clientName?.toLowerCase().includes(searchLower) ||
            note.caseNumber?.toLowerCase().includes(searchLower) ||
            note.noteDescription?.toLowerCase().includes(searchLower) ||
            note.notePlainText?.toLowerCase().includes(searchLower) ||
            note.creator?.toLowerCase().includes(searchLower) ||
            this.getCreatorName(note.creator)
              .toLowerCase()
              .includes(searchLower)
          );
        });
      }

      // Apply sorting
      notes.sort((a, b) => {
        switch (this.sortOrder) {
          case "newest":
            return new Date(b.createdDate) - new Date(a.createdDate);
          case "oldest":
            return new Date(a.createdDate) - new Date(b.createdDate);
          case "client-asc":
            return (a.clientName || "").localeCompare(b.clientName || "");
          case "client-desc":
            return (b.clientName || "").localeCompare(a.clientName || "");
          default:
            return 0;
        }
      });

      return notes;
    },
    currentPageDisplay() {
      const length = this.filteredNotes.length;
      if (length === 0) return "0";
      if (length === 1) return "1";
      return `1-${length}`;
    },
  },
  mounted() {
    this.loadFileReviewNotes();
  },
  methods: {
    async loadFileReviewNotes(cursor = null) {
      this.loading = true;
      try {
        // Get the saContactId of the selected user
        const userContactId = this.selectedUser?.saContactId || null;

        const result = await this.$apollo.query({
          query: GetFileReviewNotes,
          variables: {
            first: 100,
            after: cursor,
            userContactId: userContactId,
          },
          fetchPolicy: "network-only",
        });

        if (result.data && result.data.fileReviewNotes) {
          const { edges, pageInfo, totalCount } = result.data.fileReviewNotes;

          this.fileReviewNotes = edges.map((edge) => edge.node);
          this.totalCount = totalCount;
          this.hasNextPage = pageInfo.hasNextPage;
          this.hasPreviousPage = pageInfo.hasPreviousPage;
          this.startCursor = pageInfo.startCursor;
          this.endCursor = pageInfo.endCursor;

          // Update cursor history for back navigation
          if (cursor !== null) {
            this.cursorHistory.push(this.currentCursor);
          }
          this.currentCursor = cursor;
        }
      } catch (error) {
        console.error("Error loading file review notes:", error);
        this.$toast.error("Failed to load file review notes");
      } finally {
        this.loading = false;
      }
    },
    onUserFilterChange() {
      // Reset pagination when user filter changes
      this.cursorHistory = [];
      this.currentCursor = null;
      this.loadFileReviewNotes();
    },
    loadNextPage() {
      if (this.hasNextPage && this.endCursor) {
        this.loadFileReviewNotes(this.endCursor);
      }
    },
    loadPreviousPage() {
      if (this.cursorHistory.length > 0) {
        const previousCursor = this.cursorHistory.pop();
        this.loadFileReviewNotes(previousCursor);
      } else {
        // Go back to the beginning
        this.loadFileReviewNotes(null);
      }
    },
    formatDate(dateString) {
      if (!dateString) return "";
      try {
        return format(new Date(dateString), "MM/dd/yyyy");
      } catch (error) {
        return dateString;
      }
    },
    formatCount(count) {
      if (count >= 1000000) {
        return `${(count / 1000000).toFixed(1)}m`;
      }
      if (count >= 1000) {
        return `${(count / 1000).toFixed(1)}k`;
      }
      return count.toString();
    },
    getCreatorName(staffCode) {
      if (!staffCode || !this.users.length) return staffCode;
      const user = this.users.find((u) => u.staffCode === staffCode);
      if (user) {
        return `${user.firstName} ${user.lastName} (${user.staffCode})`;
      }
      return staffCode;
    },
    getCaseStatusColor(status) {
      if (!status) return "grey";

      const statusUpper = status.toUpperCase();

      // Color coding based on PL status codes
      // Treating: PL 00 or PL 01
      if (statusUpper.startsWith("PL 00") || statusUpper.startsWith("PL 01")) {
        return "green";
      }
      // Demand Prep: PL 02, PL 03, PL 04, PL 05, PL 07, PL 08
      else if (
        statusUpper.startsWith("PL 02") ||
        statusUpper.startsWith("PL 03") ||
        statusUpper.startsWith("PL 04") ||
        statusUpper.startsWith("PL 05") ||
        statusUpper.startsWith("PL 07") ||
        statusUpper.startsWith("PL 08")
      ) {
        // Check for Negotiation exceptions first
        if (
          statusUpper.startsWith("PL 05.1") ||
          statusUpper.startsWith("PL 08.1")
        ) {
          return "deep-orange";
        }
        return "orange";
      }
      // Negotiation: PL 05.1, PL 06, PL 08.1, PL 09
      else if (
        statusUpper.startsWith("PL 05.1") ||
        statusUpper.startsWith("PL 06") ||
        statusUpper.startsWith("PL 08.1") ||
        statusUpper.startsWith("PL 09")
      ) {
        return "deep-orange";
      }
      // Settled: PL 11, PL 12, PL 13
      else if (
        statusUpper.startsWith("PL 11") ||
        statusUpper.startsWith("PL 12") ||
        statusUpper.startsWith("PL 13")
      ) {
        return "grey darken-1";
      }
      // Fallback for other statuses
      else if (statusUpper.includes("INTAKE") || statusUpper.includes("NEW")) {
        return "blue";
      } else if (
        statusUpper.includes("INVESTIGATING") ||
        statusUpper.includes("INVESTIGATION")
      ) {
        return "cyan";
      } else if (statusUpper.includes("LITIGATION")) {
        return "red";
      } else if (statusUpper.includes("REVIEW")) {
        return "purple";
      } else {
        return "blue-grey";
      }
    },
  },
};
</script>

<style scoped>
.file-review-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px); /* Adjust based on your app bar height */
  overflow: hidden;
}

.fixed-header {
  flex-shrink: 0;
  background: white;
  z-index: 10;
  border-bottom: 1px solid #e0e0e0;
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
}

.headline {
  font-size: 24px;
  font-weight: 500;
}

.file-review-card {
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.file-review-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
}

.note-content-html {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

/* Style for HTML content in notes */
.note-content-html >>> p {
  margin-bottom: 12px;
}

.note-content-html >>> ul,
.note-content-html >>> ol {
  margin-left: 20px;
  margin-bottom: 12px;
}

.note-content-html >>> li {
  margin-bottom: 6px;
}

.note-content-html >>> strong,
.note-content-html >>> b {
  font-weight: 600;
}

.note-content-html >>> em,
.note-content-html >>> i {
  font-style: italic;
}

.note-content-html >>> a {
  color: #1976d2;
  text-decoration: none;
}

.note-content-html >>> a:hover {
  text-decoration: underline;
}

/* Custom scrollbar styling */
.scrollable-content::-webkit-scrollbar {
  width: 8px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
